import React from 'react'
// Global AppShell is applied in App.tsx for all non-public routes.
// Keep this wrapper focused on guards only.
import { Guard } from './guards'

interface AppShellWrapperProps {
  children: React.ReactNode
  requireAuth?: boolean
  requireRole?: 'admin'
  featureFlag?: string
}

/**
 * Wrapper component that provides AppShell navigation to individual routes
 * This is used to wrap page components that need the full app navigation
 */
export function AppShellWrapper({ 
  children, 
  requireAuth = true, 
  requireRole,
  featureFlag 
}: AppShellWrapperProps) {
  return (
    <Guard 
      auth={requireAuth}
      role={requireRole}
      featureFlag={featureFlag}
    >
      {children}
    </Guard>
  )
}

/**
 * Higher-order component to wrap page components with AppShell
 */
export function withAppShell<P extends object>(
  Component: React.ComponentType<P>,
  options?: {
    requireAuth?: boolean
    requireRole?: 'admin'
    featureFlag?: string
  }
) {
  const WrappedComponent = (props: P) => (
    <AppShellWrapper {...options}>
      <Component {...props} />
    </AppShellWrapper>
  )
  
  WrappedComponent.displayName = `withAppShell(${Component.displayName || Component.name})`
  
  return WrappedComponent
}
